import localizedStrings from '../autogen/LocalizedStrings';

const overrideLocaleKey: string = 'localeOverride';
const debugKey: string = 'localeDebug';
let debugLocalizedStrings: boolean = localStorage.getItem(debugKey) === 'true';

interface StringTable {
    [key: string]: string;
};

const Localization = {
    getLocalizedString: (key: string, comment: string, defaultValue?: string): string => {
        const stringTable: StringTable = currentStrings;
        if (stringTable && stringTable[key]) {
            return stringTable[key];
        }

        const fallbackValue: string = defaultValue ? defaultValue : key;
        if (debugLocalizedStrings) {
            return fallbackValue + " [" + Localization.currentLocaleDisplayName() + "] ";
        } else {
            return fallbackValue;
        }
    },

    currentLocale: (): string => {
        return currentLocale;
    },

    currentLocaleDisplayName: (): string => {
        return currentLocale && currentLocale[0] === "{" ? "custom" : currentLocale;
    },

    currentStrings: (): string => {
        return currentLocale;
    },

    overrideLocale: (): string => {
        const overrideLocale: string | null = localStorage.getItem(overrideLocaleKey) !== null ? localStorage.getItem(overrideLocaleKey) : localStorage.getItem('languagePref') !== null ? localStorage.getItem('languagePref') : null ;
        return overrideLocale ? overrideLocale : "";
    },

    setOverrideLocale: (newLocale: string | null) => {
        if (newLocale![0] === "{") {
            try {
                const customStrings = JSON.parse(newLocale!);
                newLocale = JSON.stringify(customStrings);
            } catch {
                newLocale = null;
            }
        }
        if (newLocale) {
            localStorage.setItem(overrideLocaleKey, newLocale);
        } else {
            localStorage.removeItem(overrideLocaleKey);
        }
        currentLocale = calculateLocaleWithOverride(newLocale);
        currentStrings = calculateStringsForLocale(currentLocale);
    },

    isDebugEnabled: (): boolean => {
        return debugLocalizedStrings;
    },

    setDebugEnabled: (newDebug: boolean) => {
        if (newDebug) {
            localStorage.setItem(debugKey, String(newDebug));
        } else {
            localStorage.removeItem(debugKey);
        }
        debugLocalizedStrings = newDebug;
    },
};

const calculateDefaultLocale = (): string => {
    const hasIntl = "Intl" in window && typeof Intl === "object";
    return hasIntl ? Intl.DateTimeFormat().resolvedOptions().locale : "en";
}

const validateOverrideLocale = (locale: string): string => {
    if (locale[0] === "{") {
        return locale;
    }

    if (locale.length === 2) {
        return locale;
    }

    if (locale.length > 2) {
        const separator: string = locale[2];
        if (separator === "-" || separator === "_")
            return locale;
    }

    return "";
}

const calculateLocaleWithOverride = (overrideLocale: string | null): string => {
    const defaultLocale = calculateDefaultLocale();
    const locale = overrideLocale ? validateOverrideLocale(overrideLocale) : defaultLocale;
    if (calculateStringsForLocale(locale)) {
        return locale;
    }

    for (const availableLocale in localizedStrings) {
        if (locale.startsWith(availableLocale)) {
            return availableLocale;
        }
    }

    return "en";
}

const calculateStringsForLocale = (locale: string): StringTable => {
    if (locale && locale[0] === "{") {
        return JSON.parse(locale);
    } else {
        return localizedStrings[locale]
    }
}

const calculateLocaleFromEnvironment = (): string => {
    const overrideLocale: string = Localization.overrideLocale();
    return calculateLocaleWithOverride(overrideLocale)
};

let currentLocale: string = calculateLocaleFromEnvironment();
let currentStrings: StringTable = calculateStringsForLocale(currentLocale);

export default Localization;
